import { useEffect, useState } from "react";
import { useSqlQueryWebSocket } from "../components/hooks/useSqlQueryWebSocket";
import "../assets/css/my.css";
import DSDialog from "./system-design/DSDialog";
import DSDialogTitle from "./system-design/DSDialogTitle";
import DSDialogContent from "./system-design/DSDialogContent";
import DSDialogActions from "./system-design/DSDialogActions";
import DSAlert from "./system-design/DSAlert";
import DSBox from "./system-design/DSBox";
import DSTypography from "./system-design/DSTypography";
import DSPrimaryButton from "./system-design/DSPrimaryButton";
import DSSecondaryButton from "./system-design/DSSecondaryButton";
import {
  DSTable,
  DSTableHead,
  DSTableBody,
  DSTableRow,
  DSTableCell,
} from "./system-design/Table";
import useDeviceType from "../components/hooks/useDeviceType";

interface SqlQueryStreamModalProps {
  deviceId: string;
  onClose: () => void;
}

const SqlQueryStreamModal: React.FC<SqlQueryStreamModalProps> = ({
  deviceId,
  onClose,
}) => {
  const uri = "wss://clock-socket-917257785826.us-central1.run.app/";
  const {
    start,
    stop,
    sendQuery,
    isConnected,
    error,
    loadingMessage,
    queryResults,
    isExecuting,
  } = useSqlQueryWebSocket({ uri, devId: deviceId });

  const { isSmallTablet } = useDeviceType();
  const [queryHistory, setQueryHistory] = useState<string[]>([]);
  const [lastQuery, setLastQuery] = useState<string>("");

  useEffect(() => {
    start();
  }, []);

  const handleStop = () => {
    stop();
    onClose();
  };

  const handleRunQuery = () => {
    const defaultQuery = lastQuery || "SELECT * FROM sqlite_master LIMIT 10";
    const query = window.prompt(
      "Enter SQL query:\n(Example: SELECT * FROM table_name LIMIT 10)",
      defaultQuery,
    );

    if (query && query.trim()) {
      setLastQuery(query);

      // Add to history (keep last 5 queries)
      setQueryHistory((prev) => {
        const newHistory = [query, ...prev.filter((q) => q !== query)];
        return newHistory.slice(0, 5);
      });

      sendQuery(query);
    }
  };

  const handleRunHistoryQuery = (query: string) => {
    setLastQuery(query);
    sendQuery(query);
  };

  const renderResults = () => {
    if (!queryResults) return null;

    const { columns, rows, rowCount } = queryResults;

    if (rowCount === 0) {
      return (
        <DSAlert severity="info" sx={{ mt: 2 }}>
          Query executed successfully. No rows returned.
        </DSAlert>
      );
    }

    return (
      <DSBox sx={{ mt: 2 }}>
        <DSTypography variant="subtitle2" sx={{ mb: 1 }}>
          Results ({rowCount} rows)
        </DSTypography>
        <DSBox sx={{ maxHeight: "400px", overflowY: "auto" }}>
          <DSTable size="small" sx={{ border: 1, borderColor: "divider" }}>
            <DSTableHead>
              <DSTableRow>
                {columns.map((col, idx) => (
                  <DSTableCell key={idx} sx={{ fontSize: "12px" }}>
                    {col}
                  </DSTableCell>
                ))}
              </DSTableRow>
            </DSTableHead>
            <DSTableBody>
              {rows.map((row, rowIdx) => (
                <DSTableRow key={rowIdx} hover>
                  {row.map((cell, cellIdx) => (
                    <DSTableCell key={cellIdx} sx={{ fontSize: "11px" }}>
                      {cell !== null && cell !== undefined
                        ? String(cell)
                        : "NULL"}
                    </DSTableCell>
                  ))}
                </DSTableRow>
              ))}
            </DSTableBody>
          </DSTable>
        </DSBox>
      </DSBox>
    );
  };

  return (
    <DSDialog
      open
      onClose={handleStop}
      maxWidth="lg"
      fullWidth
      fullScreen={isSmallTablet}
      sx={{ zIndex: 9999 }}
    >
      <DSDialogTitle>Stream SQL Queries</DSDialogTitle>
      <DSDialogContent>
        {/* Connection Status */}
        {loadingMessage && (
          <DSAlert severity="info" sx={{ mb: 2 }}>
            <strong>Status:</strong> {loadingMessage}
          </DSAlert>
        )}

        {/* Error Messages */}
        {error && (
          <DSAlert severity="error" sx={{ mb: 2 }}>
            <strong>Error:</strong> {error}
          </DSAlert>
        )}

        {/* Connected State */}
        {isConnected && !loadingMessage && (
          <DSBox>
            <DSAlert severity="success" sx={{ mb: 2 }}>
              <strong>Connected to device</strong> - Ready to execute queries
            </DSAlert>

            {/* Query Controls */}
            <DSBox sx={{ mb: 2 }}>
              <DSPrimaryButton
                onClick={handleRunQuery}
                disabled={isExecuting}
                sx={{ mr: "10px" }}
              >
                {isExecuting ? "Executing..." : "Run SQL Query"}
              </DSPrimaryButton>

              {queryHistory.length > 0 && (
                <DSBox sx={{ mt: 2 }}>
                  <DSTypography variant="subtitle2" sx={{ mb: 1 }}>
                    Query History:
                  </DSTypography>
                  <DSBox
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "5px",
                    }}
                  >
                    {queryHistory.map((query, idx) => (
                      <DSBox
                        key={idx}
                        sx={{
                          p: 1,
                          backgroundColor: "#161616",
                          borderRadius: 2,
                          cursor: "pointer",
                          fontSize: "12px",
                          fontFamily: "monospace",
                        }}
                        onClick={() => handleRunHistoryQuery(query)}
                        title="Click to run this query again"
                      >
                        {query}
                      </DSBox>
                    ))}
                  </DSBox>
                </DSBox>
              )}
            </DSBox>

            {/* Results Display */}
            {isExecuting && (
              <DSAlert severity="info" sx={{ mb: 2 }}>
                <strong>Executing query...</strong>
              </DSAlert>
            )}

            {renderResults()}
          </DSBox>
        )}

        {/* Initial State */}
        {!isConnected && !error && !loadingMessage && (
          <DSBox sx={{ textAlign: "center" }}>
            <DSTypography>Initializing connection to device...</DSTypography>
          </DSBox>
        )}
      </DSDialogContent>
      <DSDialogActions>
        <DSSecondaryButton onClick={handleStop}>Close</DSSecondaryButton>
      </DSDialogActions>
    </DSDialog>
  );
};

export default SqlQueryStreamModal;
